"use client"

import { useEffect } from 'react'
import { isChunkLoadError, isNextChunkAsset, scheduleChunkErrorReload } from '@/lib/chunkErrorRecovery'

/**
 * 处理 chunk 加载失败的全局错误处理器
 * 当检测到 chunk 加载失败时，自动刷新页面以获取最新版本
 *
 * 防死循环：
 * - 本会话累计自动刷新次数上限（不随时间窗口重置而“清零配额”）
 * - 同一页面生命周期内最多调度 1 次刷新（避免一次失败触发多条 error/rejection 连刷）
 */
export function ChunkErrorHandler() {
  useEffect(() => {
    const tryRecoverFromChunkError = (errorMessage: string) => {
      scheduleChunkErrorReload(errorMessage)
    }

    const handleChunkError = (event: Event) => {
      const ev = event as ErrorEvent
      const message = ev.message || ev.error?.message || ''
      const script = ev.target instanceof HTMLScriptElement ? ev.target : null
      const src = script?.src || ev.filename || ''

      const fromMessage = isChunkLoadError(message)
      const fromNextScript =
        Boolean(src) && isNextChunkAsset(src)

      if (!fromMessage && !fromNextScript) {
        return
      }

      tryRecoverFromChunkError(message || src || 'chunk script load error')
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason
      const errorMessage =
        typeof reason === 'string'
          ? reason
          : reason?.message || ''

      if (isChunkLoadError(errorMessage)) {
        event.preventDefault()
        tryRecoverFromChunkError(errorMessage)
      }
    }

    window.addEventListener('error', handleChunkError, true)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleChunkError, true)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  return null
}
